import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Eye, 
  EyeOff, 
  User, 
  Lock, 
  ArrowRight, 
  Heart, 
  Star, 
  Shield,
  Utensils,
  Mail,
  Zap,
  CheckCircle,
  Sparkles,
  MousePointer
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useParentsAuth } from '../../contexts/ParentsAuthContext';
import toast from 'react-hot-toast';

const ParentsLoginPage = () => {
  const navigate = useNavigate();
  const { signIn, authenticated } = useParentsAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (authenticated) {
      navigate('/parents-portal');
    }
  }, [authenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    try {
      setLoading(true);
      
      console.log('🔍 DEBUG: Tentando fazer login com:', formData.email);
      
      const result = await signIn(formData.email, formData.password);
      
      if (result) {
        toast.success(`Login realizado com sucesso! Bem-vindo(a), ${result.parent?.name}!`);
        navigate('/parents-portal');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Melhor tratamento de erros
      let errorMessage = 'Erro ao fazer login. Verifique suas credenciais.';
      
      if (error.message.includes('Nenhum estudante encontrado')) {
        errorMessage = 'Email não encontrado. Verifique se este é o email cadastrado como responsável do aluno.';
      } else if (error.message.includes('Senha é obrigatória')) {
        errorMessage = 'Por favor, digite sua senha.';
      } else if (error.message.includes('Erro ao buscar dados')) {
        errorMessage = 'Erro de conexão com o banco de dados. Tente novamente.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Lado esquerdo - Formulário */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative w-full max-w-md space-y-8">
          {/* Header */}
          <div className="text-center">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                  <Utensils className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>

            {/* Títulos */}
            <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
              Portal dos Pais
            </h1>
            <p className="text-gray-400">
              Acompanhe seu filho na CantinaTech
            </p>

            {/* Badge de segurança */}
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 mt-4">
              <Shield className="w-4 h-4 mr-2 text-green-400" />
              <span className="text-xs text-green-300">🔒 Portal 100% seguro</span>
            </div>
          </div>

          {/* Formulário */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">
                  E-mail
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm focus:bg-gray-800/50"
                    required
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">
                  Senha
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Sua senha"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-12 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm focus:bg-gray-800/50"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              </div>

              {/* Remember & Forgot */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-500 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="remember-me" className="ml-2 text-sm text-gray-300">
                    Lembrar de mim
                  </label>
                </div>

                <Link
                  to="/parents-portal/forgot-password"
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Esqueceu a senha?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!formData.email || !formData.password || loading}
                className="group relative w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Entrando...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Heart className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                    Entrar no Portal
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300 -z-10" />
              </button>
            </form>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-black text-gray-500">Não tem uma conta?</span>
            </div>
          </div>

          {/* Register Link */}
          <Link to="/parents-portal/register">
            <button className="group w-full bg-gradient-to-br from-gray-800 to-gray-700 border border-gray-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10">
              <div className="flex items-center justify-center">
                <Sparkles className="w-5 h-5 mr-2 group-hover:text-blue-400 transition-colors" />
                Cadastrar como Responsável
              </div>
            </button>
          </Link>

          {/* Back to Home */}
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-gray-400 hover:text-white transition-colors"
            >
              ← Voltar ao início
            </Link>
          </div>
        </div>
      </div>

      {/* Lado direito - Features/Benefits */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-black" />
        
        {/* Animated background elements */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-2xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '3s' }} />

        <div className="relative z-10 flex flex-col justify-center px-12">
          <div className="max-w-md">
            <h2 className="text-4xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Acompanhe seu filho
              </span>
              <br />
              <span className="text-white">
                em tempo real
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Portal completo para <strong className="text-white">monitorar gastos</strong> 
              e <strong className="text-white">controlar compras</strong> na cantina.
            </p>

            <div className="space-y-4">
              {[
                { text: "Saldo e gastos em tempo real", icon: Zap },
                { text: "Histórico completo de compras", icon: MousePointer },
                { text: "Recarga de créditos online", icon: Shield },
                { text: "Notificações automáticas", icon: CheckCircle }
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={index} 
                    className="flex items-center space-x-3 text-gray-300"
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span>{feature.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-gray-800/50 to-gray-700/50 rounded-2xl border border-gray-600/30 backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full border-2 border-black flex items-center justify-center text-xs">
                      👨‍👩‍👧‍👦
                    </div>
                  ))}
                </div>
                <span className="ml-3 text-sm text-gray-300">500+ famílias conectadas</span>
              </div>
              <div className="flex items-center">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                  ))}
                </div>
                <span className="ml-2 text-sm text-gray-400">4.8/5 satisfação dos pais</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentsLoginPage;